import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Tag } from 'antd';
import { CustomerServiceOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import CreateTicketModal from './CreateTicketModal';
import { getSupportTickets, TICKET_STATUS_COLORS } from '../modules/support-tickets/api';

/**
 * #132 — tickets about a place, a box or a recurring schedule (operators
 * only — the parents never render it for partner viewers). `filter` is the
 * list query (`{ collectionPointId }`, `{ siteId }` or `{ orderScheduleId }`),
 * `prefill` the CreateTicketModal context; the backend derives the rest
 * (box ⇒ its site + partner, schedule ⇒ its box + pickup site).
 */
export default function LinkedTicketsPanel({ filter, prefill, style }) {
  const { t } = useTranslation(['routes', 'common']);
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const key = JSON.stringify(filter || {});

  const load = useCallback(() => {
    if (!filter || !Object.values(filter).some(Boolean)) return;
    getSupportTickets({ ...filter, resolvedWithinDays: 30, take: 10 })
      .then(({ response }) => {
        setTickets(response?.items || []);
        setTotal(response?.total || 0);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    load();
  }, [load]);

  const seeAll = `/admin/tickets?${new URLSearchParams({ ...filter, resolved: 'all' }).toString()}`;

  return (
    <div
      className="linked-tickets-panel"
      style={{ padding: 12, border: '1px solid var(--border-color, #f0f0f0)', borderRadius: 8, ...style }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <b>
          <CustomerServiceOutlined style={{ marginRight: 6 }} />
          {t('tickets.ticketsLinked', { count: total })}
        </b>
        <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
          <Link to={seeAll}>{t('tickets.seeAll')}</Link>
          <Button size="small" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            {t('tickets.createFromHere')}
          </Button>
        </span>
      </div>
      {tickets.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {tickets.map((tk) => (
            <div key={tk.id} style={{ fontSize: 13 }}>
              <Link to={`/admin/tickets/${tk.id}`}>#{tk.id}</Link>{' '}
              <Tag color={TICKET_STATUS_COLORS[tk.status]} style={{ marginLeft: 4 }}>
                {t(`tickets.statuses.${tk.status}`, tk.status)}
              </Tag>
              {tk.subject}
              {tk.orderCode ? <span style={{ color: 'var(--text-muted)' }}> · {tk.orderCode}</span> : null}
            </div>
          ))}
        </div>
      )}
      <CreateTicketModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={load} prefill={prefill} />
    </div>
  );
}
